/**
 * Shared card visual language + floating hover panel (HEX-57).
 *
 * One source of truth for the card type / rarity language used by both the
 * out-of-combat `InventoryEditor` and the in-combat `CardCombatOverlay`:
 *   - `TYPE_META` / `RARITY_META` — icon, colour, label per card type / rarity.
 *   - `compactCost` — the terse cost string painted on the card face.
 *   - `CardHoverPanel` — a floating tooltip (replaces the old native `title=`),
 *     sticky while the pointer is over a card, dismissed on leave. Renders the
 *     full human reading of a card: name, rarity badge, type, size + rotation,
 *     cost, every effect on its own line, and the trap variant / trigger / fire
 *     model when the card is a trap.
 *   - `buildLegend` — the type-colour + rarity-pip key, identical in both views.
 *
 * Schematic visuals only — no asset pipeline, no animation library (locked
 * stack). Colours live as CSS custom properties in
 * `card-hover-panel-styles.css`; the two component stylesheets reference them so
 * the language can never drift between editor and combat.
 */

import { createGlyphElement } from '../../render/glyphs';
import { footprint } from '../grid';
import type {
  Card,
  CardEffect,
  CardType,
  EffectTarget,
  PerFaceEffect,
  Predicate,
  Rarity,
  TrapCondition,
} from '../types';
import { buildGem, cardGlyphName, cardTypeColourVar, renderCardFace } from './cardFace';

export interface CardTypeMeta {
  /** CSS custom-property reference for the type colour. */
  colourVar: string;
  label: string;
}

/** Type colour + label (ADR-0015 §3, §17). The bespoke type glyph (HEX-102,
 * `cardGlyphName`) is the visual marker now. Colours: attack red · defense blue ·
 * support purple · recursive teal · trap amber · die grey. */
export const TYPE_META: Record<CardType, CardTypeMeta> = {
  attack: { colourVar: 'var(--t-attack)', label: 'Attack' },
  defense: { colourVar: 'var(--t-defense)', label: 'Defense' },
  support: { colourVar: 'var(--t-support)', label: 'Support' },
  recursive: { colourVar: 'var(--t-recursive)', label: 'Recursive' },
  trap: { colourVar: 'var(--t-trap)', label: 'Trap' },
  die: { colourVar: 'var(--t-die)', label: 'Die' },
};

export interface CardRarityMeta {
  colourVar: string;
  label: string;
}

/** Rarity colour + label (ADR-0015 §9). The faceted gem (`buildGem`, cardFace.ts)
 * is the visual marker now. */
export const RARITY_META: Record<Rarity, CardRarityMeta> = {
  common: { colourVar: 'var(--r-common)', label: 'Common' },
  rare: { colourVar: 'var(--r-rare)', label: 'Rare' },
  epic: { colourVar: 'var(--r-epic)', label: 'Epic' },
  legendary: { colourVar: 'var(--r-legendary)', label: 'Legendary' },
};

// ── compact (on-card) text ─────────────────────────────────────────────────

/** Terse single-predicate token for the card face: `any` (≥1) / `≥4` / `≤3` /
 * `=6` / `even` / `odd`. */
export function compactPredicate(p: Predicate): string {
  switch (p.kind) {
    case 'min':
      return p.value <= 1 ? 'any' : `≥${p.value}`;
    case 'max':
      return `≤${p.value}`;
    case 'exact':
      return `=${p.value}`;
    case 'parity':
      return p.value;
  }
}

/** Compact cost line painted on the card face, e.g. `≥4 · any`. Empty string for
 * cost-less cards (traps / die fuel). */
export function compactCost(card: Card): string {
  return card.cost.map(compactPredicate).join(' · ');
}

/** Terse on-card badge for a cost-face bonus (ADR-0015 §19), e.g. `+6 block on 1`.
 * Returns the first bonus only (the card face is tight); the hover panel lists
 * them all. Empty string when the card has no `costBonuses`. */
export function costBonusBadge(card: Card): string {
  const bonus = card.costBonuses?.[0];
  if (bonus === undefined) return '';
  const gain = bonus.effects
    .map((e) => {
      if (e.kind === 'damage') return `+${e.amount} dmg`;
      if (e.kind === 'block') return `+${e.amount} block`;
      return '+bonus';
    })
    .join(' ');
  // An exact face reads cleaner bare ("on 1") than as a predicate token ("on =1").
  const face =
    bonus.facePredicate.kind === 'exact'
      ? String(bonus.facePredicate.value)
      : compactPredicate(bonus.facePredicate);
  return `${gain} on ${face}`;
}

/** Terse on-card badge for scaling dice (ADR-0015 §19), e.g. `+2 dmg/extra`.
 * Empty string when the card has no `extraCost`. */
export function extraCostBadge(card: Card): string {
  const extra = card.extraCost;
  if (extra === undefined) return '';
  const per = extra.perDie
    .map((e) => {
      if (e.kind === 'damage') return `+${e.amount} dmg`;
      if (e.kind === 'block') return `+${e.amount} block`;
      return '+bonus';
    })
    .join(' ');
  return `${per}/extra die`;
}

/**
 * Projected value of a card's primary damage / block effect given `extraDice`
 * extra dice committed past the cost slots (ADR-0015 §19). Drives the live
 * "5→7→9→11" preview on the card face. Returns null when the card has no
 * `extraCost` or no literal damage / block base effect (face-derived effects
 * can't be previewed without a roll).
 */
export function scaledPreview(
  card: Card,
  extraDice: number,
): { kind: 'damage' | 'block'; value: number } | null {
  const extra = card.extraCost;
  if (extra === undefined) return null;
  for (const kind of ['damage', 'block'] as const) {
    const base = card.effects.find((e) => e.kind === kind && e.amountFrom === undefined);
    if (base === undefined || (base.kind !== 'damage' && base.kind !== 'block')) continue;
    const per = extra.perDie
      .filter((e): e is { kind: 'damage' | 'block'; amount: number } => e.kind === kind)
      .reduce((sum, e) => sum + e.amount, 0);
    return { kind, value: base.amount + per * Math.max(0, extraDice) };
  }
  return null;
}

// ── human (panel) text ──────────────────────────────────────────────────────

/** Long-form single-predicate phrasing for the cost line. */
function humanPredicate(p: Predicate): string {
  switch (p.kind) {
    case 'min':
      return p.value <= 1 ? 'any die' : `a die ≥ ${p.value}`;
    case 'max':
      return `a die ≤ ${p.value}`;
    case 'exact':
      return `a die = ${p.value}`;
    case 'parity':
      return `an ${p.value} die`;
  }
}

/** Face-condition phrasing inside a `rollFresh` branch (`even` / `≥ 4`). */
function humanFaceCondition(p: Predicate): string {
  switch (p.kind) {
    case 'min':
      return p.value <= 1 ? 'any' : `≥ ${p.value}`;
    case 'max':
      return `≤ ${p.value}`;
    case 'exact':
      return `= ${p.value}`;
    case 'parity':
      return p.value;
  }
}

/** `Needs: a die ≥ 4 + any die`, or null when the card has no dice cost. */
export function humanCost(card: Card): string | null {
  if (card.cost.length === 0) return null;
  return `Needs: ${card.cost.map(humanPredicate).join(' + ')}`;
}

function targetWord(target: EffectTarget): string {
  return target === 'random' ? 'random side' : target;
}

/** Magnitude phrase honouring the fresh-roll `amountFrom` source. */
function amountPhrase(amount: number, from: 'faceValue' | 'faceSum' | undefined): string {
  if (from === 'faceValue') return 'the rolled face';
  if (from === 'faceSum') return 'the dice sum';
  return String(amount);
}

/** One human line per effect (top-level). `rollFresh` expands to a header line
 * plus indented per-face / on-sum branch lines. */
export function effectLines(effects: readonly CardEffect[]): string[] {
  const lines: string[] = [];
  for (const effect of effects) lines.push(...effectToLines(effect, false));
  return lines;
}

function effectToLines(effect: CardEffect, nested: boolean): string[] {
  switch (effect.kind) {
    case 'damage': {
      const target = targetWord(effect.target ?? 'opponent');
      const amt = amountPhrase(effect.amount, effect.amountFrom);
      return [`Deal ${amt} damage to ${target}`];
    }
    case 'block': {
      const target = targetWord(effect.target ?? 'self');
      const amt = amountPhrase(effect.amount, effect.amountFrom);
      return [`Gain ${amt} block — ${target}`];
    }
    case 'applyStatus': {
      const a = effect.application;
      const target = targetWord(effect.target ?? a.target);
      const mag = amountPhrase(a.magnitude, effect.magnitudeFrom);
      return [`Apply ${a.status} ${mag} for ${a.duration} turn(s) — ${target}`];
    }
    case 'rerollDice':
      return [`Reroll ${effect.count} of your dice`];
    case 'heal': {
      const target = targetWord(effect.target ?? 'self');
      return [`Restore ${effect.amount} HP — ${target}`];
    }
    case 'rollFresh': {
      const out = [`Roll ${effect.count} fresh d6`];
      for (const branch of effect.perFace ?? []) out.push(perFaceLine(branch));
      if (effect.onSum && effect.onSum.length > 0) {
        out.push(`· on sum → ${effect.onSum.map(branchEffect).join(', ')}`);
      }
      return out;
    }
    case 'armOrCharge':
      return nested ? [`arm / +${effect.amount} charge`] : [`Arm / add ${effect.amount} charge(s)`];
    case 'reflectDamage':
      return [
        `Reflect ${Math.round(effect.fraction * 100)}% of the triggering damage to its source`,
      ];
    case 'reduceBlock':
      return [`Reduce the triggering block gain by ${effect.amount}`];
    case 'mirrorStatus':
      return ['Re-apply the triggering status to its source'];
    case 'freezeCard':
      return ["Freeze the opponent's highest-cost playable card for 1 turn"];
  }
}

function perFaceLine(branch: PerFaceEffect): string {
  return `· per ${humanFaceCondition(branch.facePredicate)} → ${branch.effects
    .map(branchEffect)
    .join(', ')}`;
}

/** Compact rendering of a sub-effect inside a `rollFresh` branch. */
function branchEffect(effect: CardEffect): string {
  switch (effect.kind) {
    case 'damage':
      return `${amountPhrase(effect.amount, effect.amountFrom)} dmg ${targetWord(effect.target ?? 'opponent')}`;
    case 'block':
      return `${amountPhrase(effect.amount, effect.amountFrom)} block ${targetWord(effect.target ?? 'self')}`;
    case 'applyStatus': {
      const a = effect.application;
      return `${a.status} ${amountPhrase(a.magnitude, effect.magnitudeFrom)} ${targetWord(effect.target ?? a.target)}`;
    }
    default:
      // Non-magnitude effects collapse to their top-level phrasing.
      return effectToLines(effect, true).join('; ');
  }
}

function humanTrigger(trigger: TrapCondition): string {
  switch (trigger.kind) {
    case 'opponentRollFace':
      return `when the opponent rolls a ${trigger.value}`;
    case 'opponentAttacks':
      return 'when the opponent attacks';
    case 'opponentBlocks':
      return 'when the opponent blocks';
    case 'opponentAppliesStatus':
      return trigger.status
        ? `when the opponent applies ${trigger.status}`
        : 'when the opponent applies a status';
    case 'turnEnd':
      return 'at the end of each turn';
  }
}

/** Human lines for the ADR-0015 §19 scaling / bonus / cap fields. Empty when the
 * card carries none, so the hover panel only grows a section when relevant. */
export function tuningLines(card: Card): string[] {
  const lines: string[] = [];
  if (card.extraCost) {
    const per = effectLines(card.extraCost.perDie).join('; ').toLowerCase();
    lines.push(`Scaling: each extra ${humanFaceCondition(card.extraCost.predicate)} die → ${per}`);
  }
  for (const bonus of card.costBonuses ?? []) {
    const eff = effectLines(bonus.effects).join('; ').toLowerCase();
    lines.push(`Bonus: cost die ${humanFaceCondition(bonus.facePredicate)} → ${eff}`);
  }
  if (card.oncePerTurn) lines.push('Once per turn');
  return lines;
}

/** Human lines describing a trap's variant, trigger and fire model. */
export function trapLines(trap: NonNullable<Card['trap']>): string[] {
  const lines: string[] = [];
  switch (trap.variant) {
    case 'passive':
      lines.push('Passive trap — always armed, no dice');
      break;
    case 'loaded':
      lines.push('Loaded trap — feed a die to arm; fires once, then disarms');
      break;
    case 'charged':
      lines.push('Charged trap');
      break;
  }
  lines.push(`Triggers ${humanTrigger(trap.trigger)}`);
  if (trap.variant === 'charged') {
    const detail: string[] = [];
    if (trap.chargeMax !== undefined) detail.push(`charges up to ${trap.chargeMax}`);
    detail.push(trap.chargeSource === 'trigger' ? '+1 per trigger' : '+1 per fed die');
    if (trap.fireMode === 'atChargeMax') detail.push('fires only at max, then resets');
    lines.push(detail.join(' · '));
  }
  if (trap.maxFiresPerTurn !== undefined) {
    lines.push(`Fires at most ${trap.maxFiresPerTurn}×/turn`);
  }
  return lines;
}

// ── standalone card chip (display mode) ─────────────────────────────────────

/**
 * A standalone card for contexts that show one outside a placement grid (HEX-63
 * drop prompt, shop / merchant wares, death-screen deck). Routed through the
 * unified DISPLAY card (HEX-103), so a card looks the same here as in the combat
 * grid or inventory. Binds `hover` so pointing at it shows the full panel.
 */
export function buildCardChip(card: Card, hover: CardHoverPanel): HTMLDivElement {
  const chip = renderCardFace(card, { mode: 'display' });
  chip.classList.add('hex6-card-chip');
  chip.dataset.cardId = card.id;
  hover.bind(chip, () => ({ card, rotated: false }));
  return chip;
}

// ── legend ──────────────────────────────────────────────────────────────────

/** The type-colour + rarity-pip key. Identical markup in editor and combat so
 * the visual language is read the same way in both. */
export function buildLegend(): HTMLDivElement {
  const legend = document.createElement('div');
  legend.className = 'hex6-card-legend';

  const types = document.createElement('div');
  types.className = 'hex6-card-legend__row';
  for (const type of ['attack', 'defense', 'support', 'recursive', 'trap', 'die'] as CardType[]) {
    const meta = TYPE_META[type];
    const item = document.createElement('span');
    item.className = 'hex6-card-legend__item';
    const swatch = document.createElement('span');
    swatch.className = 'hex6-card-legend__swatch';
    swatch.style.background = meta.colourVar;
    const label = document.createElement('span');
    label.textContent = meta.label;
    item.append(swatch, label);
    types.appendChild(item);
  }

  const rarities = document.createElement('div');
  rarities.className = 'hex6-card-legend__row';
  for (const rarity of ['common', 'rare', 'epic', 'legendary'] as Rarity[]) {
    const meta = RARITY_META[rarity];
    const item = document.createElement('span');
    item.className = 'hex6-card-legend__item';
    const label = document.createElement('span');
    label.textContent = meta.label;
    item.append(buildGem(rarity, 9), label);
    rarities.appendChild(item);
  }

  legend.append(types, rarities);
  return legend;
}

// ── floating hover panel ────────────────────────────────────────────────────

const VIEWPORT_MARGIN = 8;

/** A single floating tooltip reused across every card in a view. Bind it to a
 * card element with `bind`; it shows on pointer-enter and hides on leave. */
export class CardHoverPanel {
  private el: HTMLDivElement | null = null;

  private ensure(): HTMLDivElement {
    if (this.el) return this.el;
    const el = document.createElement('div');
    el.className = 'hex6-card-panel';
    el.setAttribute('role', 'tooltip');
    el.hidden = true;
    document.body.appendChild(el);
    this.el = el;
    return el;
  }

  /** Wire `target` so hovering it shows this card's panel. `info` is read lazily
   * on each enter so callers can reflect the live placement (e.g. rotation). */
  bind(target: HTMLElement, info: () => { card: Card; rotated: boolean }): void {
    target.addEventListener('mouseenter', () => {
      const { card, rotated } = info();
      this.show(card, target, rotated);
    });
    target.addEventListener('mouseleave', () => this.hide());
  }

  show(card: Card, anchor: HTMLElement, rotated: boolean): void {
    const el = this.ensure();
    el.replaceChildren(...this.buildContent(card, rotated));
    el.hidden = false;
    this.position(anchor);
  }

  hide(): void {
    if (this.el) this.el.hidden = true;
  }

  destroy(): void {
    this.el?.remove();
    this.el = null;
  }

  private buildContent(card: Card, rotated: boolean): HTMLElement[] {
    const out: HTMLElement[] = [];

    const head = document.createElement('div');
    head.className = 'hex6-card-panel__head';
    // Bespoke type glyph + faceted gem — same visual language as the card face.
    const icon = createGlyphElement(cardGlyphName(card), {
      size: 15,
      colour: cardTypeColourVar(card),
      className: 'hex6-card-panel__icon',
    });
    const name = document.createElement('span');
    name.className = 'hex6-card-panel__name';
    name.textContent = card.name;
    const badge = document.createElement('span');
    badge.className = 'hex6-card-panel__rarity';
    badge.style.color = RARITY_META[card.rarity].colourVar;
    badge.append(buildGem(card.rarity, 9), document.createTextNode(RARITY_META[card.rarity].label));
    head.append(icon, name, badge);
    out.push(head);

    const fp = footprint(card, rotated);
    const meta = document.createElement('div');
    meta.className = 'hex6-card-panel__meta';
    meta.textContent = `${TYPE_META[card.type].label} · ${fp.cols}×${fp.rows}${
      rotated ? ' (rotated)' : ''
    }`;
    out.push(meta);

    const cost = humanCost(card);
    if (cost) {
      const costEl = document.createElement('div');
      costEl.className = 'hex6-card-panel__cost';
      costEl.textContent = cost;
      out.push(costEl);
    } else if (card.type === 'die') {
      const fuel = document.createElement('div');
      fuel.className = 'hex6-card-panel__cost';
      fuel.textContent = 'Pool fuel — adds a die to your pool';
      out.push(fuel);
    }

    const effects = effectLines(card.effects);
    if (effects.length > 0) {
      const section = document.createElement('div');
      section.className = 'hex6-card-panel__section';
      for (const text of effects) section.appendChild(this.line(text));
      out.push(section);
    }

    // §19 scaling / bonus / cap lines.
    const tuning = tuningLines(card);
    if (tuning.length > 0) {
      const section = document.createElement('div');
      section.className = 'hex6-card-panel__section hex6-card-panel__section--tuning';
      for (const text of tuning) section.appendChild(this.line(text));
      out.push(section);
    }

    if (card.trap) {
      const trapSection = document.createElement('div');
      trapSection.className = 'hex6-card-panel__section hex6-card-panel__section--trap';
      for (const text of trapLines(card.trap)) trapSection.appendChild(this.line(text));
      out.push(trapSection);
    }

    return out;
  }

  private line(text: string): HTMLDivElement {
    const line = document.createElement('div');
    line.className = 'hex6-card-panel__line';
    // Indent the rollFresh / trap branch lines for readability.
    if (text.startsWith('·')) line.classList.add('hex6-card-panel__line--sub');
    line.textContent = text;
    return line;
  }

  private position(anchor: HTMLElement): void {
    const el = this.el;
    if (!el || typeof window === 'undefined') return;
    const rect = anchor.getBoundingClientRect();
    // Measure off-screen, then clamp into the viewport.
    el.style.visibility = 'hidden';
    el.style.left = '0px';
    el.style.top = '0px';
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    let left = rect.right + VIEWPORT_MARGIN;
    if (left + pw > window.innerWidth - VIEWPORT_MARGIN) left = rect.left - pw - VIEWPORT_MARGIN;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    let top = rect.top;
    if (top + ph > window.innerHeight - VIEWPORT_MARGIN) {
      top = window.innerHeight - ph - VIEWPORT_MARGIN;
    }
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = 'visible';
  }
}
